"use client";

import Color from "@faire/web/slate/Color";
import type { IconName as FoundationIconsName } from "@faire/web/slate/icons/__generated__/foundationIcons";
import { IChevronProps } from "@faire/web/slate/icons/foundation/Chevron";
import { ILockProps } from "@faire/web/slate/icons/foundation/Lock";
import { IPriceTagProps } from "@faire/web/slate/icons/foundation/PriceTag";
import React, { type JSX } from "react";

export interface IFoundationIconProps
  extends IPriceTagProps,
    ILockProps,
    IChevronProps {
  iconName: FoundationIconsName;
  fontSize?: number;
  color?: Color;
  width?: string;
  height?: string;
}

// this icon wrapper is for Builder to render icon
export const BuilderFoundationIcon = (props: IFoundationIconProps) => {
  const [element, setElement] = React.useState<JSX.Element>();

  React.useEffect(() => {
    const lazyload = import(
      "@faire/web/slate/icons/__generated__/foundationIcons"
    );

    if (lazyload) {
      lazyload.then((module) => {
        setElement(renderIcon(module, props));
      });
    }
  }, [props]);

  return element;
};

function renderIcon(
  module: typeof import("@faire/web/slate/icons/__generated__/foundationIcons"),
  props: IFoundationIconProps
) {
  const { iconName, lockType, direction, variant, color, width, height } =
    props;

  const commonProps = {
    fontSize: props.fontSize ? `${props.fontSize}px` : undefined,
    color,
    width,
    height,
  };

  switch (iconName) {
    case "Lock":
      return <module.Lock {...commonProps} lockType={lockType ?? "square"} />;
    case "Arrow":
      return <module.Arrow {...commonProps} direction={direction ?? "up"} />;
    case "Chevron":
    case "DoubleChevron": {
      const Icon = module.DoubleChevron;
      const direction =
        props.direction === "upAndRight" ? "up" : props.direction;
      return <Icon {...commonProps} direction={direction} />;
    }
    case "PriceTag":
      return <module.PriceTag {...commonProps} variant={variant ?? "dollar"} />;
    default: {
      const Icon = module[iconName]!;
      return <Icon {...commonProps} />;
    }
  }
}
